import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';

import Overlay from '../Widget/Overlay';
import OverlayPartner from '../Widget/OverlayPartner';
import OverlayPublisher from '../Widget/OverlayPublisher';
import WidgetHeader from '../Widget/WidgetHeader';

import {
    Body as DefaultBody,
    ButtonGroup,
    Container,
    Content as DefaultContent,
    ContentContainer,
    ContentContainerMobile,
    ContentDescription,
    ContentInnerMobile,
    ContentTitle,
    FindRepLink,
    ImageContainer,
    ImageContainerMobile,
    PhoneNumber,
    Sidebar,
    SidebarTitle
} from '../DefaultStyles/Details';
import { isLongText } from '../../components/Shared/commonFunctions';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import Button from '../../components/Button';
import CssTransition from '../../components/Transition/CssTransition';
import FindLocalRepPopup from '../../components/LocalRep/FindLocalRepPopup';
import RepsNotContactable from '../../components/RepsNotContactable';
import RoundImage from '../../components/Image/RoundImage';
import TwoGroup from '../../components/TwoGroup';

import { onBackWithinCard, onGoToNextCard } from '../Widget/actions';
import { onCallComplete, onContainerClick } from './actions';
import { onFindYourRep, hideFindRepPopup, loadLocalRep } from '../LocalRep/actions';

import { getActionCompletedTime } from '../../utils/actionTiming';
import {
    getNextAction,
    buildConditionalActionsAnswerHistory
} from '../../utils/nextActionUtil';
import { CardTypes } from '../../utils/enums';
import { handleSkippingEndCard } from '../../utils/handleSkippingEndCard';
import { isPortrait, widthEm } from '../../utils/size';
import { pictureUrl } from '../../utils/imageLoading';
import { copyFor, capitalization } from '../../copy/copy';
import {
    relevantRepresentatives,
    districtInfo
} from '../../utils/localRepresentative/parser';

import { DefaultRepImageURL } from '../../containers/DefaultStyles/Shared';

import ToasterAlert from '../../components/Donation/ToasterAlert';

const Body = styled(DefaultBody)`
    margin: 1.25em 1em 1em;
`;

const Content = styled(DefaultContent)`
    margin-top: ${props => (props.isMobileResolution ? '1.5em' : '0.875em')};
    ${() => (widthEm() <= 30 ? `flex-direction: column` : ``)};
`;

const RepContainer = styled.div`
    text-align: center;
    margin-top: 22px;
    padding: 0 28px;
    float: left;
    width: ${props => `calc(100% / ${props.repCount || 1})`};
    div {
        margin: 0 auto;
    }

    ${() =>
        widthEm() <= 30 &&
        `
            width: 100%;
            padding: 0;
            text-align: left;

            > div {
                width: 80px;
                float: left;
                margin-right: 14px;

                img {
                width: 80px;
                height: 80px;`};
`;

const RepTitle = styled.h2`
    font-family: Averta;
    font-size: 16px;
    font-weight: bold;
    line-height: 1.25;
    margin-top: 12px;
    margin-bottom: 6px;

    @media (max-width: 30em) {
        margin-top: 0;
    }
`;

const TipsHeader = styled.h2`
    margin: 0;
    font-size: ${props => (isPortrait() ? (props.isLongText ? 17 : 20) : 22)}px;
    line-height: 1.25;
    padding-bottom: 10px;
`;

const Tip = styled.div`
    font-weight: normal;
    font-size: ${() => (isPortrait() ? 14 : 16)}px;
    line-height: 1.25;
`;

const NotYourRepInfo = styled.div`
    font-family: Lato;
    font-size: 0.75em;
    line-height: 1.33;
    font-weight: bold;

    ${() =>
        !isPortrait() &&
        `
            z-index: 2;
            position: absolute;
            bottom: 15px;
            left: 20px;
            width: 150px;`};
`;

const getButtonLabel = isSinglePerson => {
    return !isSinglePerson
        ? copyFor('buttons', 'finished', 'call')
        : copyFor('buttons', 'submit', 'call');
};

const normalizePhone = number => {
    return number ? '+1' + number.replace(/\D/g, '') : '';
};

const SingleCallDetails = props => (
    <Content isMobileResolution={props.isMobileResolution}>
        <Sidebar>
            <ImageContainer>
                <RoundImage src={props.people[0].photoUrl} />
            </ImageContainer>
            <SidebarTitle>
                {copyFor('sidebar', 'title', 'call', undefined, {
                    recipientName: props.people[0].name
                })}
            </SidebarTitle>
            <PhoneNumber target="_parent" href={`tel:${props.people[0].normalizedPhone}`}>
                {props.people[0].phone}
            </PhoneNumber>
        </Sidebar>
        <ContentContainer>
            <ContentContainerMobile>
                <ImageContainerMobile>
                    <RoundImage
                        isLongText={isLongText(props.people[0].name, 20)}
                        src={props.people[0].photoUrl}
                    />
                </ImageContainerMobile>
                <ContentInnerMobile>
                    <ContentTitle isHouseRep={props.useLocalRepresentative}>
                        Call {props.people[0].name}
                        <br />
                        <PhoneNumber
                            target="_parent"
                            href={`tel:${props.people[0].normalizedPhone}`}
                            onClick={() => {
                                if (props.skipEndCard) {
                                    props.handleSkippingEndCard(
                                        props.nextAction,
                                        props.cards,
                                        props.onGoToNextCard
                                    );
                                }

                                props.onCallComplete(
                                    props.apiRequestData,
                                    props.actionCompletedTime(),
                                    props.isMultiActionsCard,
                                    props.activeAction,
                                    true,
                                    props.useLocalRepresentative,
                                    props.isSinglePerson,
                                    props.people[0].name,
                                    props.skipEndCard
                                );
                            }}
                        >
                            {props.people[0].phone}
                        </PhoneNumber>
                    </ContentTitle>
                    {props.useLocalRepresentative && (
                        <NotYourRepInfo>
                            {copyFor('findReps', 'notYour', 'call')} <br />
                            <FindRepLink onClick={props.onFindYourRep}>
                                {copyFor('findReps', 'clickToFind', 'call')}
                            </FindRepLink>
                        </NotYourRepInfo>
                    )}
                </ContentInnerMobile>
            </ContentContainerMobile>
            <ContentDescription>
                <TipsHeader isLongText={isLongText(props.heading, 60)}>
                    {props.heading}
                </TipsHeader>
                {props.tips.map(tip => (
                    <Tip key={tip}>{tip}</Tip>
                ))}
            </ContentDescription>
        </ContentContainer>
    </Content>
);
SingleCallDetails.propTypes = {
    actionCompletedTime: PropTypes.func.isRequired,
    actionIdentifier: PropTypes.string.isRequired,
    activeAction: PropTypes.object.isRequired,
    apiRequestData: PropTypes.object.isRequired,
    handleSkippingEndCard: PropTypes.func.isRequired,
    heading: PropTypes.string.isRequired,
    isMobileResolution: PropTypes.bool.isRequired,
    isMultiActionsCard: PropTypes.bool.isRequired,
    onCallComplete: PropTypes.func.isRequired,
    onFindYourRep: PropTypes.func.isRequired,
    onGoToNextCard: PropTypes.func.isRequired,
    people: PropTypes.array.isRequired,
    skipEndCard: PropTypes.bool,
    tips: PropTypes.array.isRequired,
    useLocalRepresentative: PropTypes.bool
};

const SenatorsOrAllCallDetails = props => (
    <Content>
        {props.people.map(a => (
            <RepContainer key={a.phone} repCount={props.people.length}>
                <ImageContainer>
                    <RoundImage src={a.photoUrl} size="100px" />
                </ImageContainer>
                <RepTitle>{a.name}</RepTitle>
                <PhoneNumber target="_parent" href={`tel:${a.normalizedPhone}`}>
                    {a.phone}
                </PhoneNumber>
            </RepContainer>
        ))}
    </Content>
);
SenatorsOrAllCallDetails.propTypes = {
    people: PropTypes.array.isRequired
};

const CallDetails = props => (
    <Container onClick={() => props.onContainerClick(props.actionIdentifier)}>
        <Overlay />
        <OverlayPublisher />
        <OverlayPartner transitionType="right-to-left" />
        <TransitionGroup>
            {props.addressValidationError && (
                <CssTransition key="alert" in={props.addressValidationError}>
                    <ToasterAlert content={props.addressValidationError} />
                </CssTransition>
            )}
        </TransitionGroup>
        <Body>
            <WidgetHeader />
            {!props.people.length && <RepsNotContactable />}
            {!!props.people.length &&
                (props.isSinglePerson
                    ? SingleCallDetails(props)
                    : SenatorsOrAllCallDetails(props))}
            {!!props.people.length &&
                props.useLocalRepresentative &&
                !props.isMobileResolution && (
                    <NotYourRepInfo>
                        {copyFor('findReps', 'notYour', 'call')}
                        {props.isMobileResolution ? <span>&nbsp;</span> : <br />}
                        <FindRepLink onClick={props.onFindYourRep}>
                            {copyFor('findReps', 'clickToFind', 'call')}
                        </FindRepLink>
                    </NotYourRepInfo>
                )}
            {!!props.people.length && (
                <ButtonGroup
                    skipFrontCard={props.skipFrontCard}
                    isSubmitting={props.isSubmitting}
                    isPhoneLinkClicked={props.isPhoneLinkClicked}
                    isSinglePerson={props.isSinglePerson}
                >
                    <TwoGroup>
                        {!props.skipFrontCard && (
                            <Button
                                label={copyFor(
                                    'buttons',
                                    'back',
                                    'call',
                                    capitalization.eachWord
                                )}
                                onClick={() =>
                                    props.onBackWithinCard(
                                        props.actionIdentifier,
                                        props.card,
                                        true,
                                        true
                                    )
                                }
                                className="middleButton"
                            />
                        )}
                        <Button
                            primary={true}
                            label={getButtonLabel(props.isSinglePerson)}
                            type="button"
                            className="submitButton"
                            normalizedPhone={
                                props.isSinglePerson
                                    ? props.people[0].normalizedPhone
                                    : ''
                            }
                            isMobileResolution={props.isMobileResolution}
                            isSubmitting={props.isSubmitting}
                            onClick={e => {
                                if (/CriOS/i.test(navigator.userAgent)) {
                                    e.preventDefault();
                                    window.location = `tel:${
                                        props.people[0].normalizedPhone
                                    }`;
                                }

                                if (props.skipEndCard) {
                                    props.handleSkippingEndCard(
                                        props.nextAction,
                                        props.cards,
                                        props.onGoToNextCard
                                    );
                                }

                                props.onCallComplete(
                                    props.apiRequestData,
                                    props.actionCompletedTime(),
                                    props.isMultiActionsCard,
                                    props.activeAction,
                                    false,
                                    props.useLocalRepresentative,
                                    props.isSinglePerson,
                                    props.people[0].name,
                                    props.skipEndCard
                                );
                            }}
                        />
                        <LoadingSpinner />
                    </TwoGroup>
                </ButtonGroup>
            )}
        </Body>
        <TransitionGroup>
            {props.showFindYouRepPopup && (
                <CssTransition key="alert" in={props.showFindYouRepPopup}>
                    <FindLocalRepPopup
                        address={props.address}
                        submitting={props.isLocalRepPopupLoading}
                        hideFindRepPopup={props.hideFindRepPopup}
                        onSubmit={() =>
                            props.loadLocalRep(
                                props.endUserLocation,
                                encodeURI(props.address),
                                props.representativeSearch,
                                true
                            )
                        }
                    />
                </CssTransition>
            )}
        </TransitionGroup>
    </Container>
);

CallDetails.propTypes = {
    ...SingleCallDetails.propTypes,
    ...SenatorsOrAllCallDetails.propTypes,
    activeAction: PropTypes.object.isRequired,
    address: PropTypes.string.isRequired,
    addressValidationError: PropTypes.any,
    actionIdentifier: PropTypes.string.isRequired,
    isMobileResolution: PropTypes.bool.isRequired,
    onBackWithinCard: PropTypes.func.isRequired,
    onGoToNextCard: PropTypes.func.isRequired,
    handleSkippingEndCard: PropTypes.func.isRequired,
    onCallComplete: PropTypes.func.isRequired,
    actionCompletedTime: PropTypes.func.isRequired,
    apiRequestData: PropTypes.object.isRequired,
    isSubmitting: PropTypes.bool.isRequired,
    isLocalRepPopupLoading: PropTypes.bool.isRequired,
    isPhoneLinkClicked: PropTypes.bool.isRequired,
    card: PropTypes.object.isRequired,
    isMultiActionsCard: PropTypes.bool.isRequired,
    onFindYourRep: PropTypes.func.isRequired,
    hideFindRepPopup: PropTypes.func.isRequired,
    showFindYouRepPopup: PropTypes.bool.isRequired,
    loadLocalRep: PropTypes.func.isRequired,
    isSinglePerson: PropTypes.bool.isRequired,
    people: PropTypes.array.isRequired,
    representativeSearch: PropTypes.object.isRequired,
    useLocalRepresentative: PropTypes.bool,
    skipFrontCard: PropTypes.bool,
    skipEndCard: PropTypes.bool,
    onContainerClick: PropTypes.func,
    endUserLocation: PropTypes.object
};

export default connect(
    state => {
        const nextAction = getNextAction(
            state.widget.actions,
            state.widget.activeAction.identifier,
            buildConditionalActionsAnswerHistory(state),
            state.widget.cards
        );
        let people;
        if (!state.widget.activeAction.representativeSearch) {
            people = [
                {
                    name: state.widget.activeAction.recipientMask,
                    phone: state.widget.activeAction.recipients,
                    normalizedPhone: normalizePhone(state.widget.activeAction.recipients),
                    photoUrl: pictureUrl(state)
                }
            ];
        } else {
            const reps = state.localRep.localRepData
                ? relevantRepresentatives(
                      state.widget.activeAction.representativeSearch,
                      state.localRep.localRepData.representative
                  )
                : [];
            const callableReps = reps.filter(rep => rep.phone).slice(0, 3);
            people = callableReps.map(rep => ({
                name: `${rep.title || ''} ${rep.fullName}${districtInfo(rep)}`,
                phone: rep.phone,
                normalizedPhone: rep.normalizedPhone,
                photoUrl: rep.photoUrl || DefaultRepImageURL
            }));
        }

        return {
            activeAction: state.widget.activeAction,
            address: state.form.google_autocomplete
                ? state.form.google_autocomplete.values.address
                : '',
            addressValidationError: state.widget.addressValidationError,
            endUserLocation: state.widget.endUserLocation,
            nextAction,
            actionIdentifier: state.widget.activeAction.identifier,
            pictureUrl: pictureUrl(state),
            people,
            isMobileResolution: state.widget.isMobileResolution,
            apiRequestData: state.widget.apiRequestData,
            actionCompletedTime: getActionCompletedTime(state, 'call'),
            card: state.widget.cards[state.widget.activeAction.order - 1],
            cards: state.widget.cards,
            isMultiActionsCard:
                state.widget.cards[state.widget.activeAction.order - 1].type ===
                CardTypes.multipleActions,
            isMultiTargettedCard:
                state.widget.cards[state.widget.activeAction.order - 1].type ===
                CardTypes.targetedMultiAction,
            tips: (state.widget.activeAction.tips || '')
                .split(/(\r\n|\n|\r)/gm)
                .filter(t => t && t !== '\r\n' && t !== '\r' && t !== '\n'),
            isSubmitting: state.call.isSubmitting,
            isLocalRepPopupLoading: state.localRep.isLocalRepPopupLoading,
            isPhoneLinkClicked: state.call.isPhoneLinkClicked,
            showFindYouRepPopup: state.localRep.showFindYouRepPopup,
            isSinglePerson: people.length === 1,
            representativeSearch: state.widget.maximumRepresentativeSearch,
            useLocalRepresentative: !!state.widget.activeAction.representativeSearch,
            skipFrontCard: state.widget.activeAction.skipFrontCard,
            skipEndCard: state.widget.activeAction.skipEndCard && !!nextAction,
            heading:
                state.widget.activeAction.customText ||
                copyFor('defaultText', 'heading', 'call')
        };
    },
    {
        onBackWithinCard,
        onGoToNextCard,
        handleSkippingEndCard,
        onCallComplete,
        onFindYourRep,
        hideFindRepPopup,
        loadLocalRep,
        onContainerClick
    }
)(CallDetails);
